import {
  escapeHTML,
  track,
  debounce,
  registerServiceWorker,
  placeholderImage,
  trackMediaLoad,
  usefulMediaLabel,
} from './common.js';

const body = document.body;
const page = body.dataset.page;

function usefulMediaName(item = {}) {
  return usefulMediaLabel(item.name);
}

if (page === 'archive') {
  const results = document.querySelector('#archive-page-results');
  const empty = document.querySelector('[data-archive-empty]');
  const input = document.querySelector('#archive-page-search');
  const count = document.querySelector('[data-result-count]');
  let albums = [];
  let collections = [];
  let posts = [];
  let selectedType = 'all';
  const initialParams = new URLSearchParams(location.search);
  let selectedCollection = initialParams.get('collection') || '';
  const initialQuery = initialParams.get('q') || '';
  input.value = initialQuery;

  function archiveItems() {
    const albumItems = albums.map((album) => ({
      type: 'album',
      album,
      title: album.title,
      description: album.description,
      search: [album.title, album.description, album.visibility, ...(album.media || []).map((item) => item.name)].filter(Boolean).join(' ')
    }));
    const imageItems = albums.flatMap((album) => (album.media || [])
      .filter((item) => item.kind === 'image')
      .map((item, index) => ({
        type: 'image',
        album,
        item,
        index,
        title: usefulMediaName(item) || `${album.title} / frame ${String(index + 1).padStart(2, '0')}`,
        description: album.title,
        search: [item.name, album.title, album.description].filter(Boolean).join(' ')
      })));
    const journalItems = posts.map((post) => ({
      type: 'journal',
      post,
      title: post.title,
      description: post.excerpt,
      search: [post.title, post.excerpt, ...(post.tags || [])].filter(Boolean).join(' ')
    }));
    return [...albumItems, ...imageItems, ...journalItems];
  }

  function resultMarkup(entry, index) {
    if (entry.type === 'album') {
      const album = entry.album;
      const cover = album.media?.[0];
      const media = cover?.kind === 'video'
        ? `<video src="${escapeHTML(cover.url)}" muted playsinline preload="metadata" aria-label="${escapeHTML(album.title)} cover"></video>`
        : cover
          ? `<img src="${escapeHTML(cover.url)}" alt="${escapeHTML(album.title)} album cover" loading="${index < 3 ? 'eager' : 'lazy'}" decoding="async" />`
          : `<img src="${placeholderImage()}" alt="" width="1200" height="800" />`;
      const fileCount = album.media?.length || 0;
      return `<article class="archive-card archive-card--album">
        <a class="archive-card__link" href="/album?id=${encodeURIComponent(album.id)}">
          <span class="archive-card__media">${media}<span class="archive-card__badge">Album · ${album.visibility === 'public' ? 'Public' : 'Shared'}</span></span>
          <span class="archive-card__copy"><p>${fileCount} FILE${fileCount === 1 ? '' : 'S'} · ${escapeHTML(album.visibility)}</p><h2>${escapeHTML(album.title)}</h2><span>↗</span></span>
        </a>
      </article>`;
    }
    if (entry.type === 'image') {
      return `<article class="archive-card archive-card--image">
        <a class="archive-card__link" href="/album?id=${encodeURIComponent(entry.album.id)}&frame=${entry.index + 1}">
          <span class="archive-card__media"><img src="${escapeHTML(entry.item.url)}" alt="${escapeHTML(entry.title)}" loading="${index < 6 ? 'eager' : 'lazy'}" decoding="async" /><span class="archive-card__badge">Image</span></span>
          <span class="archive-card__copy"><p>FROM ${escapeHTML(entry.album.title)}</p><h2>${escapeHTML(entry.title)}</h2><span>↗</span></span>
        </a>
      </article>`;
    }
    const post = entry.post;
    const cover = post.cover?.url
      ? `<img src="${escapeHTML(post.cover.url)}" alt="${escapeHTML(post.cover.alt || post.title)}" loading="${index < 3 ? 'eager' : 'lazy'}" decoding="async" />`
      : `<img src="${placeholderImage()}" alt="" width="1200" height="800" />`;
    return `<article class="archive-card archive-card--journal">
      <a class="archive-card__link" href="/journal/${encodeURIComponent(post.slug)}">
        <span class="archive-card__media">${cover}<span class="archive-card__badge">Journal</span></span>
        <span class="archive-card__copy"><p>${escapeHTML(post.publishedAt || post.updatedAt || '')} · ${Number(post.readingMinutes || 1)} MIN READ</p><h2>${escapeHTML(post.title)}</h2><span>↗</span></span>
      </a>
    </article>`;
  }

  const reportSearch = debounce((query, type, results) => track('archive_page_search', { query, type, results }), 600);

  function renderResults(query = '', { report = true } = {}) {
    const words = query.toLowerCase().split(/\s+/).filter(Boolean);
    const collection = collections.find((item) => item.id === selectedCollection || item.slug === selectedCollection);
    const visible = archiveItems().filter((entry) => {
      if (selectedType !== 'all' && entry.type !== selectedType) return false;
      if (collection) {
        if (entry.type === 'journal') return false;
        if (!(collection.albumIds || []).includes(entry.album.id)) return false;
      }
      if (!query && selectedType === 'all') return entry.type === 'album';
      const haystack = String(entry.search || '').toLowerCase();
      return words.every((word) => haystack.includes(word));
    });
    results.innerHTML = visible.map(resultMarkup).join('');
    results.querySelectorAll('img, video').forEach(trackMediaLoad);
    results.removeAttribute('aria-busy');
    count.textContent = `${visible.length} RESULT${visible.length === 1 ? '' : 'S'}`;
    empty.hidden = visible.length > 0;
    if (report) reportSearch(query, selectedType, visible.length);
  }

  function renderCollections() {
    const root = document.querySelector('[data-archive-collections]');
    const grid = document.querySelector('[data-archive-collection-grid]');
    if (!root || !grid) return;
    const visible = collections.filter((collection) => (collection.albumIds || []).some((id) => albums.some((album) => album.id === id)));
    root.hidden = visible.length === 0;
    document.querySelector('[data-archive-collection-count]').textContent = String(visible.length).padStart(2, '0');
    grid.innerHTML = visible.map((collection) => {
      const firstAlbum = albums.find((album) => (collection.albumIds || []).includes(album.id));
      const cover = collection.coverUrl || firstAlbum?.media?.[0]?.url || '';
      return `<button type="button" class="archive-collection ${selectedCollection === collection.id ? 'is-active' : ''}" data-archive-collection="${escapeHTML(collection.id)}">
        ${cover ? `<img src="${escapeHTML(cover)}" alt="" loading="lazy" />` : ''}
        <span><small>${collection.albumIds.length} ALBUM${collection.albumIds.length === 1 ? '' : 'S'}</small><b>${escapeHTML(collection.title)}</b></span>
      </button>`;
    }).join('');
  }

  async function loadArchive() {
    try {
      const [albumResponse, blogResponse] = await Promise.all([
        fetch('/api/albums', { cache: 'no-store' }),
        fetch('/api/blog', { cache: 'no-store' })
      ]);
      const [data, blogData] = await Promise.all([
        albumResponse.json().catch(() => ({})),
        blogResponse.json().catch(() => ({}))
      ]);
      if (!albumResponse.ok) throw new Error(data.error || 'Archive unavailable');
      albums = (data.albums || []).filter((album) => album.status === 'published');
      collections = data.collections || [];
      posts = blogResponse.ok ? (blogData.posts || []) : [];
      renderCollections();
      const examples = [...albums.map((album) => album.title), ...posts.map((post) => post.title), ...albums.flatMap((album) => (album.media || []).map(usefulMediaName))]
        .filter((value, index, values) => value && value.length > 2 && /[a-z\u00c0-\u024f]{2}/i.test(value) && !/^\d+$/.test(value) && values.indexOf(value) === index)
        .slice(0, 3);
      input.placeholder = examples.length ? `Try “${examples.join('”, “')}”…` : 'Search the archive…';
      renderResults(input.value.trim(), { report: Boolean(input.value.trim()) });
    } catch (_) {
      results.innerHTML = '';
      count.textContent = '0 RESULTS';
      empty.textContent = 'The archive is momentarily unavailable.';
      empty.hidden = false;
    }
  }

  function commitSearch() {
    const query = input.value.trim();
    const url = new URL(location.href);
    if (query) url.searchParams.set('q', query);
    else url.searchParams.delete('q');
    history.replaceState({}, '', url);
    renderResults(query);
  }

  document.querySelector('[data-archive-search]').addEventListener('click', commitSearch);
  input.addEventListener('input', () => renderResults(input.value.trim()));
  input.addEventListener('keydown', (event) => {
    if (event.key === 'Enter') commitSearch();
  });
  document.querySelector('[data-archive-types]')?.addEventListener('click', (event) => {
    const button = event.target.closest('[data-archive-type]');
    if (!button) return;
    selectedType = button.dataset.archiveType;
    document.querySelectorAll('[data-archive-type]').forEach((item) => item.classList.toggle('is-active', item === button));
    renderResults(input.value.trim());
  });
  document.querySelector('[data-archive-collection-grid]')?.addEventListener('click', (event) => {
    const button = event.target.closest('[data-archive-collection]');
    if (!button) return;
    selectedCollection = selectedCollection === button.dataset.archiveCollection ? '' : button.dataset.archiveCollection;
    const url = new URL(location.href);
    if (selectedCollection) url.searchParams.set('collection', selectedCollection);
    else url.searchParams.delete('collection');
    history.replaceState({}, '', url);
    renderCollections();
    renderResults(input.value.trim());
  });
  loadArchive();
}

if (page === 'media-kit') {
  async function loadMediaKit() {
    try {
      const response = await fetch('/api/site-content', { cache: 'no-store' });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'Media kit unavailable');
      const content = data.content?.mediaKit || {};
      const file = data.files?.['media-kit'];
      if (content.title) document.querySelector('[data-kit-title]').textContent = content.title;
      if (content.description) document.querySelector('[data-kit-description]').textContent = content.description;
      if (content.fileName) document.querySelector('[data-kit-file-name]').textContent = content.fileName;
      if (content.fileMeta) document.querySelector('[data-kit-file-meta]').textContent = content.fileMeta;
      const download = document.querySelector('[data-kit-download]');
      const action = document.querySelector('[data-kit-action]');
      if (file?.url) {
        download.href = file.url;
        download.removeAttribute('aria-disabled');
        action.textContent = 'Download media kit';
        download.addEventListener('click', () => track('media_kit_page_download'));
      } else {
        action.textContent = 'File being updated';
      }
    } catch (_) {
      document.querySelector('[data-kit-action]').textContent = 'File being updated';
    }
  }
  loadMediaKit();
}

const cursorDot = document.querySelector('.section-cursor--dot');
const cursorRing = document.querySelector('.section-cursor--ring');
let cursorX = 0;
let cursorY = 0;
let ringX = -17;
let ringY = -17;
let cursorTarget = null;
const finePointer = matchMedia('(hover:hover) and (pointer:fine)');
if (!finePointer.matches) body.classList.add('is-touch');
window.addEventListener('pointermove', (event) => {
  if (event.pointerType !== 'mouse' || !finePointer.matches) {
    body.classList.add('is-touch');
    body.classList.remove('has-pointer');
    return;
  }
  body.classList.remove('is-touch');
  cursorX = event.clientX;
  cursorY = event.clientY;
  cursorTarget = document.elementFromPoint(cursorX, cursorY)?.closest('a,button,input') || null;
  cursorRing.classList.toggle('is-targeting', Boolean(cursorTarget));
  body.classList.add('has-pointer');
});
let cursorFrame = 0;
function animateCursor() {
  ringX += (cursorX - 17 - ringX) * .18;
  ringY += (cursorY - 17 - ringY) * .18;
  cursorDot.style.transform = `translate(${cursorX - 2.5}px,${cursorY - 2.5}px)`;
  cursorRing.style.transform = `translate(${ringX}px,${ringY}px) scale(${cursorTarget ? 1.28 : 1})`;
  cursorFrame = requestAnimationFrame(animateCursor);
}
function startCursor() {
  if (cursorFrame || !finePointer.matches || matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  cursorFrame = requestAnimationFrame(animateCursor);
}
function stopCursor() {
  if (!cursorFrame) return;
  cancelAnimationFrame(cursorFrame);
  cursorFrame = 0;
}
// Never keep a render loop alive on a backgrounded tab.
document.addEventListener('visibilitychange', () => (document.hidden ? stopCursor() : startCursor()));
startCursor();
registerServiceWorker();
document.querySelector('[data-section-top]')?.addEventListener('click', () => window.scrollTo({ top: 0, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' }));
track(`${page}_page_view`);
